import type { MutationHookOptions } from "@kv/apollo-client";
import { useCallback } from "react";
import { useAuthTokensContext } from "~/context/AuthTokens";
import type {
  GraphqlNode,
  CreateEntityMutationVars,
  UpdateEntityMutationVars,
  DeleteEntityMutationVars,
  DeleteEntitiesMutationVars,
  GenericMutationVars,
  CreateManyMutationVars,
} from "~/types";

import type { MutationWithStateArgs } from "./mutations";
import { useMutationWithState } from "./mutations";



export const useAuthenticatedMutationWithState = <
  MutationResultsData,
  MutationVars,
  MutationArgs extends unknown[],
>({
  mutationNode,
  options,
  getMutationVars,
}: MutationWithStateArgs<MutationResultsData, MutationVars, MutationArgs>) => {
  const { authToken } = useAuthTokensContext();

  const getAuthenticatedMutationVars = useCallback(
    (...args: MutationArgs) => ({
      ...getMutationVars(...args),
      token: authToken,
    }),
    [getMutationVars, authToken],
  );

  const mutationWithStateTuple = useMutationWithState<
    MutationResultsData,
    MutationVars,
    MutationArgs
  >({
    mutationNode,
    options,
    getMutationVars: getAuthenticatedMutationVars,
  });

  return mutationWithStateTuple;
};

export const makeCreateEntityMutationHook = <
  MutationResultsData,
  MutationInput,
>(
  mutationNode: GraphqlNode<
    MutationResultsData,
    CreateEntityMutationVars<MutationInput>
  >,
) => {
  const useCreateEntityMutation = (
    options?: MutationHookOptions<
      MutationResultsData,
      CreateEntityMutationVars<MutationInput>
    >,
  ) => {
    const getMutationVars = useCallback(
      (input: MutationInput) => ({ input }),
      [],
    );

    const mutationWithStateTuple = useAuthenticatedMutationWithState<
      MutationResultsData,
      CreateEntityMutationVars<MutationInput>,
      [MutationInput]
    >({
      mutationNode,
      options,
      getMutationVars,
    });

    return mutationWithStateTuple;
  };

  return useCreateEntityMutation;
};

export const makeUpdateEntityMutationHook = <
  MutationResultsData,
  MutationInput,
>(
  mutationNode: GraphqlNode<
    MutationResultsData,
    UpdateEntityMutationVars<MutationInput>
  >,
) => {
  const useUpdateEntityMutation = (
    options?: MutationHookOptions<
      MutationResultsData,
      UpdateEntityMutationVars<MutationInput>
    >,
  ) => {
    const getMutationVars = useCallback(
      (entityId: string, input: MutationInput) => ({
        id: entityId,
        input,
      }),
      [],
    );

    const mutationWithStateTuple = useAuthenticatedMutationWithState<
      MutationResultsData,
      UpdateEntityMutationVars<MutationInput>,
      [string, MutationInput]
    >({
      mutationNode,
      options,
      getMutationVars,
    });

    return mutationWithStateTuple;
  };

  return useUpdateEntityMutation;
};



export const makeCreateManyEntityMutationHook = <
  MutationResultsData,
  MutationInput,
>(
  mutationNode: GraphqlNode<
    MutationResultsData,
    CreateManyMutationVars<MutationInput>
  >,
) => {
  const useCreateManyEntityMutation = (
    options?: MutationHookOptions<
      MutationResultsData,
      CreateManyMutationVars<MutationInput>
    >,
  ) => {
    const getMutationVars = useCallback(
      (input: MutationInput[]) => ({
        input,
      }),
      [],
    );

    const mutationWithStateTuple = useAuthenticatedMutationWithState<
      MutationResultsData,
      CreateManyMutationVars<MutationInput>,
      [MutationInput[]]
    >({
      mutationNode,
      options,
      getMutationVars,
    });

    return mutationWithStateTuple;
  };

  return useCreateManyEntityMutation;
};

export const makeDeleteEntityMutationHook = <MutationResultsData>(
  mutationNode: GraphqlNode<MutationResultsData, DeleteEntityMutationVars>,
) => {
  const useDeleteEntitiesMutation = (
    options?: MutationHookOptions<
      MutationResultsData,
      DeleteEntityMutationVars
    >,
  ) => {
    const getMutationVars = useCallback((id: string) => ({ id }), []);

    const mutationWithStateTuple = useAuthenticatedMutationWithState<
      MutationResultsData,
      DeleteEntityMutationVars,
      [string]
    >({
      mutationNode,
      options,
      getMutationVars,
    });

    return mutationWithStateTuple;
  };

  return useDeleteEntitiesMutation;
};

export const makeDeleteEntitiesMutationHook = <MutationResultsData>(
  mutationNode: GraphqlNode<MutationResultsData, DeleteEntitiesMutationVars>,
) => {
  const useCustomMutation = (
    options?: MutationHookOptions<
      MutationResultsData,
      DeleteEntitiesMutationVars
    >,
  ) => {
    const getMutationVars = useCallback((ids: string[]) => ({ ids }), []);

    const mutationWithStateTuple = useAuthenticatedMutationWithState<
      MutationResultsData,
      DeleteEntitiesMutationVars,
      [string[]]
    >({
      mutationNode,
      options,
      getMutationVars,
    });

    return mutationWithStateTuple;
  };

  return useCustomMutation;
};

export const makeAuthenticatedMutationHook = <
  MutationResultsData,
  MutationInput,
>(
  mutationNode: GraphqlNode<
    MutationResultsData,
    GenericMutationVars<MutationInput>
  >,
) => {
  const useMutation = (
    options?: MutationHookOptions<
      MutationResultsData,
      GenericMutationVars<MutationInput>
    >,
  ) => {
    const getMutationVars = useCallback(
      (input: MutationInput) => ({ input }),
      [],
    );

    const mutationWithStateTuple = useAuthenticatedMutationWithState<
      MutationResultsData,
      GenericMutationVars<MutationInput>,
      [MutationInput]
    >({
      mutationNode,
      options,
      getMutationVars,
    });

    return mutationWithStateTuple;
  };

  return useMutation;
};
